<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { categoryList, categoryAdd } from "@/common/api/rest/category";

import type {
  CategoryType,
  CategoryPayloadType,
} from "@/common/types/category";

import { PanelTheme } from "@/common/themes";
import { FormCategory } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();

const listCategory = ref<CategoryType[]>([]);
const payload = ref<CategoryPayloadType | null>(null);

const showModalAdd = ref(false);
const payloadValid = ref(false);

function fetchData() {
  categoryList("SERVICES")
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        listCategory.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  categoryAdd(payload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        addItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function goToCategory(category_id: string) {
  router.push(`/services/${category_id}/edit`);
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="services-page">
    <panel-theme>
      <div class="page-header">
        <div class="title">
          <svg-icon icon="services" size="2rem" />
          {{ t("common.labels.services") }} |
          {{ t("category.labels.categories") }}
        </div>

        <div class="actions">
          <button class="btn btn-md btn-primary" @click="addItem()">
            <svg-icon icon="add" size="1.25rem" />
            {{ t("category.labels.category").toUpperCase() }}
          </button>
        </div>
      </div>

      <div class="box">
        <div class="box-list">
          <template
            v-for="category in listCategory"
            :key="category.category_id"
          >
            <div
              class="box-list-item"
              @click="goToCategory(category.category_id)"
            >
              <div class="box-list-item-title">
                {{ category.name }}
              </div>
              <div
                class="box-list-item-description"
                v-html="category.description"
              />
            </div>
          </template>
        </div>
      </div>
    </panel-theme>

    <base-modal
      :show="showModalAdd"
      :title="t('category.modals.titles.add_category').toUpperCase()"
      :show-button-confirm="true"
      :button-confirm="t('common.labels.save')"
      :button-close="t('common.labels.cancel')"
      @close="addItemCancel()"
      @confirm="addItemConfirm()"
    >
      <template #content>
        <form-category
          type="SERVICES"
          @update:data="payload = $event"
          @validate="payloadValid = $event"
        />
      </template>
    </base-modal>
  </article>
</template>

<style lang="scss" scoped>
.services-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-block: 0.75rem;
    padding-inline: 0.75rem;
    background: #ffffff;
    border-radius: 0.5rem;
    margin-block-end: 1.54rem;

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--text-medium);
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
    }
  }

  .box {
    display: flex;
    flex-direction: column;
    gap: 1.54rem;
    background: #ffffff;
    border-radius: 0.5rem;
    padding: 1.54rem;

    &-title {
      font-size: 1.25rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--text-medium);
      margin-block-end: 0.75rem;
    }

    &-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
      gap: 1rem;

      &-item {
        flex: 1 1 200px;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        border: 2px solid var(--border);
        border-radius: 0.5rem;
        padding-block: 1rem;
        padding-inline: 1.25rem;
        cursor: pointer;

        &:hover {
          border-color: var(--primary);
        }

        &-title {
          font-size: 1.25rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        &-description {
          font-size: 1rem;
          color: var(--text-light);
        }
      }
    }
  }
}
</style>